import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Button, Form } from 'grab-ui';
import savedSvg from 'public/images/saved.svg';
import InputField from 'components/v2/input-field/input-field';
import AddressField from 'components/v2/address-field/new';
import MapWithDragSearch from 'components/v2/map-with-drag-search';
import { selectedCitySelector } from 'selectors/app-selector';
import {
  currentEditingPOISelector,
  poisFromSavedPOISelector,
} from 'selectors/geo-selectors';
import { CityPropType } from 'common/prop-types';
import I18n from 'i18n-js';
import cx from 'classnames';
import GeoDuck from 'ducks/geo-duck';
import ScribeSavedPoiDuck from 'ducks/scribe-saved-poi-duck';
import closeIcon from 'public/icons/close.svg';
import _some from 'lodash/some';
import deleteBinSvg from 'public/icons/delete-bin.svg';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import _includes from 'lodash/includes';
import _isEmpty from 'lodash/isEmpty';
import { NONE_EDITING_LABEL } from 'constants/geo-constants';
import styles from './styles.less';
import { addressDetailFormField } from './form-validator';
import OverlayBody from '../overlay-body';
import { useFetchSearchAddressFunc } from '../common-hook';
import { generateAddressDetailUpdate, getDisplayLabel } from './helper';

export const CommonInputField = ({ form, icon, field, value, disabled }) => {
  useEffect(
    () => {
      if (field.fieldName && form.getFieldValue(field.fieldName) !== value) {
        // Sync form with redux data
        form.setFieldsValue({ [field.fieldName]: value });
      }
    },
    [form, field, value],
  );

  return (
    <div className={styles.inputContainer}>
      {icon && <img className={styles.img} src={icon} alt="" />}
      <InputField
        id={`${field.fieldName}`}
        fieldClassName={cx(styles.inputField, { [styles.hasIcon]: !!icon })}
        form={form}
        fieldOptions={{
          initialValue: value,
          validateTrigger: 'onBlur',
          rules: field.makeRules({}),
        }}
        placeholder={I18n.t(field.placeholder)}
        autoComplete={field.name}
        disabled={disabled}
      />
    </div>
  );
};

CommonInputField.defaultProps = {
  icon: undefined,
  value: undefined,
  disabled: false,
};

CommonInputField.propTypes = {
  form: PropTypes.shape({}).isRequired,
  icon: PropTypes.string,
  field: PropTypes.shape({}).isRequired,
  value: PropTypes.string,
  disabled: PropTypes.bool,
};

const OverlayFooter = ({
  title,
  cancelButtonText,
  onCancel,
  confirmButtonText,
  onConfirm,
  confirmButtonType,
}) => (
  <div className={styles.footer}>
    <span>
      <span>{title}</span>
      <div className={styles.buttonContainer}>
        <Button className={styles.button} onClick={onCancel}>
          {cancelButtonText}
        </Button>
        <Button
          className={styles.button}
          type={confirmButtonType}
          onClick={onConfirm}
        >
          {confirmButtonText}
        </Button>
      </div>
    </span>
  </div>
);
OverlayFooter.defaultProps = {
  confirmButtonType: undefined,
};

OverlayFooter.propTypes = {
  title: PropTypes.string.isRequired,
  cancelButtonText: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  confirmButtonText: PropTypes.string.isRequired,
  onConfirm: PropTypes.func.isRequired,
  confirmButtonType: PropTypes.string,
};

const AddressDetailPanel = ({ dispatch, city, form, currentEditingPOI }) => {
  const { currentPOI, originalPOI, postSavingPoiCallback } =
    currentEditingPOI || {};
  const favoritesPOIs = useSelector(poisFromSavedPOISelector);
  const [deletePOI, setDeletePOI] = useState(false);
  const [changePOILabel, setChangePOILabel] = useState(false);
  const [
    changeExistingPOIWithSameLabel,
    setChangeExistingPOIWithSameLabel,
  ] = useState(false);

  const isAllowToDeletePOI = useMemo(
    () => currentPOI && currentPOI.label && _isEqual(currentPOI, originalPOI),
    [originalPOI, currentPOI],
  );

  const isChangingAddress = useMemo(
    () =>
      originalPOI &&
      currentPOI &&
      _isEqual(originalPOI.label, currentPOI.label) &&
      !_isEqual(originalPOI.id, currentPOI.id),
    [originalPOI, currentPOI],
  );

  const isDisabled = useMemo(
    () => {
      const fieldsError = form.getFieldsError();
      return (
        !_get(currentPOI, 'id') ||
        !form.getFieldValue(addressDetailFormField.savedPlaceLabel.fieldName) ||
        _some(Object.values(fieldsError), error => error && error.length > 0)
      );
    },
    [currentPOI, form],
  );

  const existingPOIWithSameLabel = useMemo(
    () => {
      const currentPOILabel = _get(currentPOI, 'label');
      const currentPOIID = _get(currentPOI, 'id');
      if (_isEmpty(favoritesPOIs) || !currentPOILabel) {
        return undefined;
      }
      return favoritesPOIs.find(
        poi => poi.label === currentPOILabel && poi.id !== currentPOIID,
      );
    },
    [favoritesPOIs, currentPOI],
  );

  const displayMapLatLng = useMemo(
    () => {
      if (currentPOI) {
        return currentPOI.latlng;
      }
      return undefined;
    },
    [currentPOI],
  );

  const onDeletePOIAction = useCallback(
    () => {
      setDeletePOI(true);
    },
    [setDeletePOI],
  );

  const postSavingPoiCallbackFunc = useCallback(
    (newPOI, isDelete) => {
      postSavingPoiCallback && postSavingPoiCallback(newPOI, isDelete);
      dispatch(GeoDuck.creators.closeAddressDetailOverlay());
    },
    [dispatch, postSavingPoiCallback],
  );

  useEffect(() => {
    dispatch(ScribeSavedPoiDuck.creators.startEditingSavedPOI());
  }, []);

  const onSavePOI = useCallback(
    () => {
      // Step 1 Sync the update to BE
      dispatch(ScribeSavedPoiDuck.creators.savePOI());
      dispatch(GeoDuck.creators.saveCurrentPOI());
      postSavingPoiCallbackFunc(currentPOI, false);
    },
    [dispatch, currentPOI, postSavingPoiCallbackFunc],
  );

  const onDeletePOI = useCallback(
    () => {
      dispatch(ScribeSavedPoiDuck.creators.deleteSavedPOI());
      dispatch(GeoDuck.creators.deleteCurrentPOI());
      postSavingPoiCallbackFunc(currentPOI, true);
    },
    [dispatch, postSavingPoiCallbackFunc],
  );

  const onConfirmClick = useCallback(
    () => {
      if (isChangingAddress) {
        // Show double confirm when changing poi
        setChangePOILabel(true);
      }
      if (existingPOIWithSameLabel) {
        // Show double confirm to replace existing label
        setChangeExistingPOIWithSameLabel(true);
      } else {
        onSavePOI();
      }
    },
    [
      isChangingAddress,
      setChangePOILabel,
      existingPOIWithSameLabel,
      setChangeExistingPOIWithSameLabel,
      onSavePOI,
    ],
  );

  const onClose = useCallback(
    () => {
      dispatch(ScribeSavedPoiDuck.creators.cancelEditingSavedPOI());
      dispatch(GeoDuck.creators.closeAddressDetailOverlay());
    },
    [dispatch],
  );

  const fetchAddressFunc = useFetchSearchAddressFunc({ isPickUp: false });

  const onSelectPOI = useCallback(
    newPOI => {
      if (!_isEqual(currentPOI, newPOI)) {
        dispatch(
          ScribeSavedPoiDuck.creators.editSavedPOIField(
            'ADDRESS',
            _get(newPOI, 'id'),
          ),
        );
        dispatch(GeoDuck.creators.setCurrentEditingPOI(newPOI));
        if (currentPOI) {
          // Copy current label and ext to new POI
          const updateData = generateAddressDetailUpdate(currentPOI, newPOI);
          Object.entries(updateData).forEach(([fn, fv]) => {
            dispatch(GeoDuck.creators.updateCurrentEditingPOIInfo(fn, fv));
          });
        }
      }
    },
    [dispatch, currentPOI],
  );

  const onCancelFooterAction = useCallback(
    () => {
      setChangePOILabel(false);
      setDeletePOI(false);
      setChangeExistingPOIWithSameLabel(false);
    },
    [setChangePOILabel, setDeletePOI, setChangeExistingPOIWithSameLabel],
  );

  const footer = useMemo(
    () => {
      if (deletePOI) {
        return (
          <OverlayFooter
            onCancel={onCancelFooterAction}
            onConfirm={onDeletePOI}
            title={`${I18n.t(
              'phrase.savedPoi.express_web_savedpoi_button_remove_prominence',
            )} '${getDisplayLabel(_get(originalPOI, 'label'))}'?`}
            cancelButtonText={I18n.t(
              'phrase.savedPoi.express_web_savedpoi_button_cancel',
            )}
            confirmButtonText={I18n.t(
              'phrase.savedPoi.express_web_savedpoi_button_remove_prominence',
            )}
            confirmButtonType={'danger'}
          />
        );
      }
      if (changePOILabel) {
        return (
          <OverlayFooter
            onCancel={onCancelFooterAction}
            onConfirm={onSavePOI}
            title={I18n.t(
              'phrase.savedPoi.express_web_savedpoi_body_change_address',
              {
                addressLabel: getDisplayLabel(_get(originalPOI, 'label')),
              },
            )}
            cancelButtonText={I18n.t(
              'phrase.savedPoi.express_web_savedpoi_button_cancel',
            )}
            confirmButtonText={I18n.t(
              'phrase.savedPoi.express_web_savedpoi_button_change',
            )}
            confirmButtonType={'primary'}
          />
        );
      }
      if (changeExistingPOIWithSameLabel) {
        return (
          <OverlayFooter
            onCancel={onCancelFooterAction}
            onConfirm={onSavePOI}
            title={I18n.t(
              'phrase.savedPoi.express_web_savedpoi_body_change_address',
              {
                addressLabel: getDisplayLabel(
                  _get(existingPOIWithSameLabel, 'label'),
                ),
              },
            )}
            cancelButtonText={I18n.t(
              'phrase.savedPoi.express_web_savedpoi_button_cancel',
            )}
            confirmButtonText={I18n.t(
              'phrase.savedPoi.express_web_savedpoi_button_change',
            )}
            confirmButtonType={'primary'}
          />
        );
      }
      return undefined;
    },
    [
      deletePOI,
      changePOILabel,
      changeExistingPOIWithSameLabel,
      existingPOIWithSameLabel,
      onCancelFooterAction,
      onSavePOI,
      onDeletePOI,
      originalPOI,
    ],
  );

  // small screen to handle keyboard (keyboard +- 300px)
  // focusing only small mobile viewport
  const smallScreen = useMemo(
    () => window.innerHeight <= 675 && window.innerWidth <= 767,
    [],
  );

  return (
    <OverlayBody
      showConfirm={!footer}
      disabledConfirm={isDisabled}
      onConfirmClick={onConfirmClick}
      showClose
      onClose={onClose}
      confirmButtonText={
        _get(originalPOI, 'label')
          ? I18n.t(
              'phrase.savedPoi.express_web_savedpoi_button_confirm_prominence',
            )
          : I18n.t(
              'phrase.savedPoi.express_web_savedpoi_button_save_prominence',
            )
      }
      showFooter={!!footer}
      footer={footer}
    >
      <a className={styles.closeButton} onClick={onClose}>
        <img src={closeIcon} alt="close" />
      </a>
      {city && (
        <MapWithDragSearch
          key={'drag-map'}
          city={city}
          latlng={displayMapLatLng}
          onMapSearchResult={onSelectPOI}
          className={cx({ [styles.biggerMap]: smallScreen })}
        />
      )}
      <div
        className={cx(styles.addressDetailContainer, {
          [styles.smallerInput]: smallScreen,
        })}
      >
        <CommonInputField
          form={form}
          icon={savedSvg}
          field={addressDetailFormField.savedPlaceLabel}
          value={getDisplayLabel(_get(currentPOI, 'label'))}
          disabled={_includes(NONE_EDITING_LABEL, _get(currentPOI, 'label'))}
        />
        <div className={styles.addressField}>
          <AddressField
            city={city}
            onChange={onSelectPOI}
            poi={currentPOI}
            isPickUp={false}
            showNumbers={false}
            fetchAddressFunc={fetchAddressFunc}
            allowDelete={false}
            placeholder={I18n.t(
              'v2.locationDrawer.placeholder.addDropOffLocation',
            )}
            autoFocus={false}
            searchInOverlay={smallScreen}
          />
        </div>
        <CommonInputField
          form={form}
          field={addressDetailFormField.addressDetail}
          value={_get(currentPOI, 'sp_ext_info.address_details')}
        />
        <CommonInputField
          form={form}
          field={addressDetailFormField.locationNote}
          value={_get(currentPOI, 'sp_ext_info.note_to_driver')}
        />
        <CommonInputField
          form={form}
          field={addressDetailFormField.contactName}
          value={_get(currentPOI, 'sp_ext_info.contact_name')}
        />
        <CommonInputField
          form={form}
          field={addressDetailFormField.contactNumber}
          value={_get(currentPOI, 'sp_ext_info.contact_number')}
        />
        {!footer &&
          isAllowToDeletePOI && (
            <div className={styles.delete}>
              <img className={styles.icon} src={deleteBinSvg} alt={''} />
              <Button className={styles.text} onClick={onDeletePOIAction}>
                {I18n.t(
                  'phrase.savedPoi.express_web_savedpoi_button_remove_prominence',
                )}
              </Button>
            </div>
          )}
      </div>
    </OverlayBody>
  );
};

AddressDetailPanel.defaultProps = {
  city: undefined,
  poi: undefined,
  currentEditingPOI: undefined,
};

AddressDetailPanel.propTypes = {
  dispatch: PropTypes.func.isRequired,
  form: PropTypes.shape({}).isRequired,
  city: CityPropType,
  currentEditingPOI: PropTypes.shape({}),
};

const onValuesChange = ({ dispatch }, changedFields, _) => {
  Object.entries(changedFields).forEach(([fn, fv]) => {
    dispatch(ScribeSavedPoiDuck.creators.editSavedPOIField(fn, fv));
    dispatch(GeoDuck.creators.updateCurrentEditingPOIInfo(fn, fv));
  });
};

export default connect(state => ({
  city: selectedCitySelector(state),
  currentEditingPOI: currentEditingPOISelector(state),
}))(Form.create({ onValuesChange })(AddressDetailPanel));
